import * as React from 'react'

import { action, makeObservable, observable, runInAction } from 'mobx'
import { observer } from 'mobx-react'
import type { PDFDocumentProxy } from 'pdfjs-dist'

import PdfDocument from '~/client/src/shared/components/PdfDocument/PdfDocument'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import countUndecodedImages from '~/client/src/shared/utils/pdfUndecodedImages'
import { toast } from '~/client/src/shared/utils/toast'

import PdfPageSelectorDialog from './dialogs/PdfPageSelectorDialog'

// The rasterized page is stored as one image and shown to every viewer as a
// single Mapbox texture / Konva canvas. iOS caps both at ~4096px, so the long
// edge must stay within that ceiling; below it we render as sharp as the
// sheet's physical size allows (up to a sane DPI). A vector PDF has no native
// resolution — it's rasterized at whatever scale we pick.
const MAX_RENDER_LONG_EDGE_PX = 4096
const MAX_RENDER_DPI = 300
const PDF_POINTS_PER_INCH = 72
const UNDECODED_IMAGES_TOAST_KEY = 'pdf-undecoded-images'
const UNDECODED_IMAGES_TOAST_TIMEOUT = 8000

interface IProps {
  pdfFile: File
  uploadImage: (img: Blob) => Promise<void>
}

@observer
export default class PdfToImageConverter extends React.Component<IProps> {
  @observable private isDocumentLoaded: boolean = false
  @observable private isPageSelectorDialogShown: boolean = false
  @observable private pageNumber: number = 1
  @observable private renderScale: number = null

  private pdfDocument: PDFDocumentProxy = null
  private pdfInput: HTMLDivElement

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public render() {
    const { pdfFile } = this.props

    return (
      <>
        <PdfDocument
          file={pdfFile}
          containerClassName="hidden"
          pageNumber={this.pageNumber}
          scale={this.renderScale}
          devicePixelRatio={1}
          isPageHidden={
            this.isPageSelectorDialogShown || this.renderScale === null
          }
          setPageRef={this.setInputRef}
          onPageRenderSuccess={this.convertPdfToImage}
          onLoadSuccess={this.onDocumentLoadSuccess}
        />
        {this.pdfPageSelector}
      </>
    )
  }

  private get pdfPageSelector(): JSX.Element {
    if (!this.isDocumentLoaded || !this.isPageSelectorDialogShown) {
      return null
    }

    return (
      <PdfPageSelectorDialog
        pdfFile={this.props.pdfFile}
        onDialogSubmit={this.onDialogSubmit}
      />
    )
  }

  @action.bound
  private async onDocumentLoadSuccess(pdf: PDFDocumentProxy) {
    this.pdfDocument = pdf
    this.isDocumentLoaded = true

    if (pdf.numPages > 1) {
      this.showPageSelectorDialog()
    } else {
      await this.updateRenderScale(this.pageNumber)
    }
  }

  @action.bound
  private async onDialogSubmit(pageNumberToConvert: number) {
    this.pageNumber = pageNumberToConvert
    this.hidePageSelectorDialog()
    await this.updateRenderScale(pageNumberToConvert)
  }

  // Rasterize the selected page so its long edge reaches the target DPI but
  // never exceeds the viewer-safe pixel ceiling. Aspect ratio is preserved;
  // devicePixelRatio is pinned to 1 so the output blob is exactly this size.
  private async updateRenderScale(pageNumber: number) {
    const page = await this.pdfDocument.getPage(pageNumber)
    const { width, height } = page.getViewport({ scale: 1 }) // PDF points
    const longEdgePt = Math.max(width, height)
    const longEdgeInches = longEdgePt / PDF_POINTS_PER_INCH
    const targetLongEdgePx = Math.min(
      MAX_RENDER_LONG_EDGE_PX,
      longEdgeInches * MAX_RENDER_DPI,
    )

    runInAction(() => {
      this.renderScale = targetLongEdgePx / longEdgePt
    })
  }

  @action.bound
  private showPageSelectorDialog() {
    this.isPageSelectorDialogShown = true
  }

  @action.bound
  private hidePageSelectorDialog() {
    this.isPageSelectorDialogShown = false
  }

  private convertPdfToImage = async () => {
    if (!this.pdfInput) {
      return
    }

    const imgFile = await new Promise<Blob>(resolve =>
      (this.pdfInput.children[0] as HTMLCanvasElement).toBlob(blob =>
        resolve(blob),
      ),
    )

    // A page whose images failed to decode still renders — and still converts
    // to a perfectly valid blob — just without them. Import what we did get,
    // but say so, otherwise the only signal is a silently incomplete sitemap.
    const page = await this.pdfDocument.getPage(this.pageNumber)
    if (countUndecodedImages(page)) {
      toast.info(Localization.translator.pdfPageImagesCouldNotBeRendered, {
        timeout: UNDECODED_IMAGES_TOAST_TIMEOUT,
        key: UNDECODED_IMAGES_TOAST_KEY,
      })
    }

    await this.props.uploadImage(imgFile)
  }

  private setInputRef = (ref: HTMLDivElement) => {
    this.pdfInput = ref
  }
}
